import React from 'react';
import { ChevronUpIcon, ChevronDownIcon } from '@heroicons/react/24/outline';
import { TableLoader } from './LoadingSpinner';
import Pagination from './Pagination';
import { useTranslation } from 'react-i18next';

const Table = ({
  columns = [],
  data = [],
  loading = false,
  sortable = false,
  sortBy,
  sortDirection,
  onSort,
  pagination,
  onPageChange,
  emptyMessage,
  className = '',
}) => {
  const { t } = useTranslation();
  const resolvedEmptyMessage = emptyMessage || t('tables.noDataAvailable');
  // Normalize columns: support both { key, title } and { accessor, Header }
  const normalizedColumns = columns.map((col) => ({
    ...col,
    key: col.key ?? col.accessor,
    title: col.title ?? col.Header,
  }));

  const handleSort = (column) => {
    if (!sortable || !column.sortable || !onSort) return;
    
    let newDirection = 'asc';
    if (sortBy === column.key && sortDirection === 'asc') {
      newDirection = 'desc';
    }
    
    onSort(column.key, newDirection);
  };

  // Filter out actions column for card display (will be shown separately)
  const displayColumns = normalizedColumns.filter(col => col.key !== 'actions');
  const actionsColumn = normalizedColumns.find(col => col.key === 'actions');

  if (loading) {
    return <TableLoader rows={5} cols={normalizedColumns.length} />;
  }

  // Card view: used when viewport is narrow so data fits without horizontal scroll
  const CardView = () => {
    if (data.length === 0) {
      return (
        <div className="text-center py-12 text-gray-500 xl:hidden">
          <p className="text-sm">{resolvedEmptyMessage}</p>
        </div>
      );
    }

    return (
      <div className="space-y-4 xl:hidden w-full min-w-0 max-w-full overflow-x-hidden">
        {data.map((row, rowIndex) => (
          <div
            key={row.id || rowIndex}
            data-load-id={row.id}
            className="bg-white border border-gray-200 rounded-lg shadow-sm p-4 space-y-2.5 hover:shadow-md transition-shadow w-full min-w-0"
          >
            {displayColumns.map((column) => {
              const value = row[column.key];
              const renderedValue = column.render ? column.render(value, row) : value;
              
              // Skip rendering if value is null/undefined and no custom render
              if (!column.render && (value === null || value === undefined || value === '')) {
                return null;
              }
              
              return (
                <div key={column.key} className="flex items-start gap-2 py-1.5 w-full min-w-0 overflow-hidden">
                  <div className="text-xs font-semibold text-gray-400 uppercase tracking-wide flex-shrink-0 break-words" style={{ width: '140px', minWidth: '140px', maxWidth: '140px' }}>
                    {column.title}:
                  </div>
                  <div className={`text-sm text-gray-900 flex-1 min-w-0 overflow-hidden break-words ${
                    column.align === 'center' ? 'text-center' : 
                    column.align === 'right' ? 'text-right' : 'text-left'
                  }`} style={{ wordBreak: 'break-word', overflowWrap: 'break-word' }}>
                    {renderedValue || <span className="text-gray-400 italic">-</span>}
                  </div>
                </div>
              );
            })}
            {actionsColumn && (
              <div className="flex items-center gap-2 pt-2 pb-1 border-t border-gray-200 mt-2 -mx-4 -mb-4 px-4 py-3 bg-gray-50 rounded-b-lg w-full min-w-0 overflow-x-auto overflow-y-visible">
                <div className="text-xs font-semibold text-gray-400 uppercase tracking-wide flex-shrink-0 break-words" style={{ width: '140px', minWidth: '140px', maxWidth: '140px' }}>
                  {actionsColumn.title}:
                </div>
                <div className="table-card-actions flex flex-1 min-w-0 flex-wrap items-center justify-start gap-2">
                  {actionsColumn.render && actionsColumn.render(null, row)}
                </div>
              </div>
            )}
          </div>
        ))}
      </div>
    );
  };

  return (
    <div className={`${className} w-full min-w-0 max-w-full`}>
      {/* Card view: default for small/medium viewports — no horizontal scroll */}
      <CardView />

      {/* Table view: only on xl and up so data fits without horizontal scroll */}
      <div className="hidden xl:block table-container overflow-x-auto w-full min-w-0 -mx-4 sm:mx-0">
        <table className="table-fixed w-full divide-y divide-gray-200" style={{ tableLayout: 'fixed', wordWrap: 'break-word' }}>
        <thead className="bg-gray-50">
          <tr>
            {normalizedColumns.map((column) => (
              <th
                key={column.key}
                className={`
                  table-header px-2 py-3 text-xs sm:text-sm align-top
                  ${sortable && column.sortable ? 'cursor-pointer hover:bg-gray-100' : ''}
                  ${column.align === 'center' ? 'text-center' : column.align === 'right' ? 'text-right' : 'text-left'}
                  ${column.sticky ? 'sticky right-0 bg-gray-50 z-10 shadow-[-1px_0_0_0_rgba(229,231,235,1)]' : ''}
                `}
                style={{
                  ...(column.width ? { width: column.width, minWidth: column.minWidth ?? column.width } : {}),
                  wordBreak: 'break-word',
                  overflowWrap: 'break-word'
                }}
                onClick={() => handleSort(column)}
              >
                <div className="flex flex-wrap items-center gap-1 min-w-0">
                  <span className="break-words">{column.title}</span>
                  {sortable && column.sortable && (
                    <div className="ml-2 flex flex-col">
                      <ChevronUpIcon 
                        className={`w-3 h-3 ${
                          sortBy === column.key && sortDirection === 'asc' 
                            ? 'text-blue-600' 
                            : 'text-gray-400'
                        }`}
                      />
                      <ChevronDownIcon 
                        className={`w-3 h-3 -mt-1 ${
                          sortBy === column.key && sortDirection === 'desc' 
                            ? 'text-blue-600' 
                            : 'text-gray-400'
                        }`}
                      />
                    </div>
                  )}
                </div>
              </th>
            ))}
          </tr>
        </thead>
        <tbody className="bg-white divide-y divide-gray-200">
          {data.length === 0 ? (
            <tr>
              <td 
                colSpan={normalizedColumns.length} 
                className="px-4 sm:px-6 py-8 sm:py-12 text-center text-sm sm:text-base text-gray-500"
              >
                {resolvedEmptyMessage}
              </td>
            </tr>
          ) : (
            data.map((row, rowIndex) => (
              <tr key={row.id || rowIndex} data-load-id={row.id} className="hover:bg-gray-50 group">
                {normalizedColumns.map((column) => {
                  const isActionsCol = column.key === 'actions';
                  return (
                  <td
                    key={column.key}
                    className={`
                      table-cell px-2 py-3 text-xs sm:text-sm align-top
                      ${column.sticky || isActionsCol ? '' : 'min-w-0'}
                      ${column.align === 'center' ? 'text-center' : column.align === 'right' ? 'text-right' : 'text-left'}
                      ${column.sticky ? 'sticky right-0 bg-white group-hover:bg-gray-50 z-10 shadow-[-1px_0_0_0_rgba(229,231,235,1)]' : ''}
                    `}
                    style={{
                      ...(column.width ? { width: column.width, minWidth: column.minWidth ?? column.width } : {}),
                      ...(!isActionsCol ? { wordBreak: 'break-word', overflowWrap: 'break-word' } : {}),
                    }}
                  >
                    <div
                      className={
                        isActionsCol
                          ? 'min-w-0 w-full max-w-full overflow-visible'
                          : 'min-w-0 w-full max-w-full break-words overflow-hidden'
                      }
                      style={
                        isActionsCol
                          ? undefined
                          : { wordBreak: 'break-word', overflowWrap: 'break-word' }
                      }
                    >
                      {column.render ? column.render(row[column.key], row) : row[column.key]}
                    </div>
                  </td>
                  );
                })}
              </tr>
            ))
          )}
        </tbody>
      </table>
      
      {pagination && (
        <div className="px-4 sm:px-6 py-3 bg-white border-t border-gray-200">
          <Pagination
            {...pagination}
            onPageChange={onPageChange}
          />
        </div>
      )}
      </div>

      {/* Pagination for card view */}
      {pagination && (
        <div className="xl:hidden px-4 py-3 bg-white border-t border-gray-200 mt-4">
          <Pagination
            {...pagination}
            onPageChange={onPageChange}
          />
        </div>
      )}
    </div>
  );
};

export default Table;